import {
  LitElement,
  css,
  html,
  classMap,
  styleMap,
  ref,
  createRef,
  enableBodyScroll,
  disableBodyScroll,
} from '@hh/global.js'

class HHDetailsModal extends LitElement {
  static properties = {
    breakpoint: {
      type: String,
    },
    label: {
      type: String,
    },
    selector: {
      type: String,
    },
    open: {
      type: Boolean,
      attribute: 'is-open',
      reflect: true,
    },
    forcedOpen: {
      attribute: 'open',
    },
    lockHeight: {
      type: Boolean,
      attribute: 'lock-height',
    },
    heightOffset: {
      type: Number,
      attribute: 'height-offset',
    },
    _height: {
      state: true,
    },
  }

  $container = createRef()
  $details = createRef()

  constructor() {
    super()

    this.open = false
    this.lockHeight = false
    this.heightOffset = 3
    this.breakpoint = '(min-width: 750px)'
    this.bodyListener = this.bodyListener.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()
    document.body.addEventListener('closeModal', this.close.bind(this))
  }

  requestUpdate(name, value) {
    // Allows parents to close open modals
    if (name === 'forcedOpen') {
      this.toggle(false, !this.forcedOpen)
    }
    return super.requestUpdate(name, value)
  }

  interceptEvent(e) {
    e._intercepted = true
  }

  toggle(e = false, value = undefined) {
    if (e.type === 'keyup' && e.code.toUpperCase() !== 'ENTER') {
      return true
    }

    e && e.preventDefault()

    this.open = typeof value === 'undefined' ? !this.open : value
    this[`${this.open ? 'add' : 'remove'}BodyListener`]()
    this.open && this.lockHeight && this.setHeight()
    this.open && this.broadcast()
  }

  closeFromDetails(e) {
    const path = e.composedPath()
    if (
      path.some((element) => {
        return element.matches && element.matches('[data-close]')
      })
    ) {
      this.toggle()
    }
  }

  closeWithEscape(e) {
    if (e.code.toUpperCase() === 'ESCAPE') {
      this.toggle()
    }
  }

  close() {
    this.open && this.toggle()
  }

  isMobile() {
    return !window.matchMedia(this.breakpoint).matches
  }

  addBodyListener() {
    this.isMobile() && disableBodyScroll(this.$container?.value)
    document.body.addEventListener('click', this.bodyListener)
  }

  removeBodyListener() {
    enableBodyScroll(this.$container?.value)
    document.body.removeEventListener('click', this.bodyListener)
  }

  bodyListener(e) {
    !e._intercepted && this.toggle()
  }

  setHeight() {
    const { top: parentTop = 0 } = this.parentNode.getBoundingClientRect() || {}
    const { top: detailsTop = 0 } =
      this.$details?.value.getBoundingClientRect() || {}

    this._height = this.isMobile()
      ? `calc(100vh - ${Math.floor(
          parentTop + detailsTop - this.heightOffset
        )}px)`
      : ''
  }

  broadcast() {
    this.dispatchEvent(
      new CustomEvent('modalOpened', {
        bubbles: true,
        composed: true,
        detail: {
          value: this,
        },
      })
    )
  }

  render() {
    return html` <!-- @ts-ignore no-incompatible-type-binding -->
      <details
        ?open="${this.open}"
        @click="${this.interceptEvent}"
        ${ref(this.$container)}
      >
        <summary
          @keyup="${this.toggle}"
          aria-expanded="${this.open}"
          aria-haspopup="dialog"
          role="button"
          ${ref(this.$container)}
        >
          <slot
            @click="${this.toggle}"
            class="${classMap({ 'is-open': this.open })}"
            name="summary"
            tabindex="-1"
          ></slot>
        </summary>
        <div class="backdrop" aria-hidden="true"></div>
        <div
          class="content"
          role="dialog"
          aria-modal="true"
          aria-label="${this.label}"
          style="${styleMap({ height: this._height })}"
          ${ref(this.$details)}
        >
          <hh-focus-trap active="${this.open}">
            <slot
              name="details"
              @keyup="${this.closeWithEscape}"
              @click="${this.closeFromDetails}"
            ></slot>
          </hh-focus-trap>
        </div>
      </details>`
  }

  static styles = css`
    * {
      box-sizing: border-box;
    }
    summary {
      list-style-type: none;
    }

    summary:focus {
      outline: 0.2rem solid var(--hh-color-focus);
      outline-offset: var(--hh-focus-outline-offset);
    }

    summary::-webkit-details-marker {
      display: none;
    }
    .backdrop {
      position: fixed;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      background-color: rgba(0, 0, 0, 0.3);
      backdrop-filter: blur(6px);
      -webkit-backdrop-filter: blur(6px);
    }
    .content {
      position: var(--hh-position, absolute);
      top: var(--hh-top);
      right: 0;
      width: 44rem;
      background: var(--hh-modal-background);
      height: var(--hh-modal-height);
      z-index: var(--hh-z-index-inner, 2);
      padding: var(--hh-spacing-xl) var(--hh-spacing-2xl);
    }

    ::slotted([slot='details']) {
      width: 100%;
      display: flex;
    }

    :host {
      z-index: var(--hh-z-index, 100);
      height: 100%;
    }
  `
}

if (!customElements.get('hh-details-modal')) {
  customElements.define('hh-details-modal', HHDetailsModal);
}